"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

interface AudioVisualizationState {
    isPlaying: boolean;
    beatIntensity: number;
    bassEnergy: number;
    midEnergy: number;
    highEnergy: number;
    isBeat: boolean;
}

interface AudioVisualizationContextType extends AudioVisualizationState {
    registerAudioElement: (audioElement: HTMLAudioElement) => void;
    unregisterAudioElement: () => void;
    playbackCurrentTime: number;
    playbackDuration: number;
    playbackProgress: number; // 0..1
    visualizerSensitivity: number; // 0.5 (gentle) … 2.0 (aggressive)
    setVisualizerSensitivity: (value: number) => void;
    bpmEstimate: number | null;
    visualizerRange: number; // 0.5 (narrow) … 2.0 (wide)
    setVisualizerRange: (value: number) => void;
    sensitivityMin: number;
    sensitivityMax: number;
    rangeMin: number;
    rangeMax: number;
}

const AudioVisualizationContext = createContext<AudioVisualizationContextType | undefined>(undefined);

export function AudioVisualizationProvider({ children, visualizerSensitivity: initialSensitivity = 1, visualizerRange: initialRange = 1, sensitivityMin = 0.5, sensitivityMax = 2.0, rangeMin = 0.5, rangeMax = 2.0 }: { children: React.ReactNode; visualizerSensitivity?: number; visualizerRange?: number; sensitivityMin?: number; sensitivityMax?: number; rangeMin?: number; rangeMax?: number }) {
    // Tunable sensitivity config
    const FFT_SIZE = 2048;
    const SMOOTHING = 0.45; // lower = more responsive, tighter follow
    const HISTORY_SIZE = 24; // shorter history for faster adaptation
    const THRESHOLD_MULTIPLIER = 1.12; // lower = more sensitive to peaks
    const VARIANCE_FACTOR = 1.1; // lower = more sensitive to sudden spikes
    const BASE_BEAT_MIN_INTERVAL_MS = 90; // base refractory period when no BPM yet
    const MIN_WIDTH_PX = 1200; // run visualizer only on wide screens
    const MAX_UPDATE_FPS = 120; // cap UI state updates for performance
    const PLAYBACK_UPDATE_FPS = 30; // progress bar updates

    const [state, setState] = useState<AudioVisualizationState>({
        isPlaying: false,
        beatIntensity: 0,
        bassEnergy: 0,
        midEnergy: 0,
        highEnergy: 0,
        isBeat: false,
    });

    const [playback, setPlayback] = useState<{ currentTime: number; duration: number; progress: number }>({
        currentTime: 0,
        duration: 0,
        progress: 0,
    });
    const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(val, max));
    const [visualizerSensitivity, _setVisualizerSensitivity] = useState<number>(clamp(initialSensitivity, sensitivityMin, sensitivityMax));
    const [visualizerRange, _setVisualizerRange] = useState<number>(clamp(initialRange, rangeMin, rangeMax));
    const setVisualizerSensitivity = (value: number) => _setVisualizerSensitivity(clamp(value, sensitivityMin, sensitivityMax));
    const setVisualizerRange = (value: number) => _setVisualizerRange(clamp(value, rangeMin, rangeMax));

    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
    const currentAudioElementRef = useRef<HTMLAudioElement | null>(null);
    // Cache source nodes per audio element
    const sourceNodeMapRef = useRef<WeakMap<HTMLMediaElement, MediaElementAudioSourceNode>>(new WeakMap());
    const animationFrameRef = useRef<number | null>(null);
    const beatHistoryRef = useRef<number[]>([]);
    const lastBeatTimeRef = useRef<number>(0);
    const energyHistoryRef = useRef<number[]>([]);
    const dataArrayRef = useRef<Uint8Array | null>(null);
    const lastUiUpdateRef = useRef<number>(0);
    const mediaQueryRef = useRef<MediaQueryList | null>(null);
    const isEnvironmentEligibleRef = useRef<boolean>(false);
    const beatTimestampsRef = useRef<number[]>([]);
    const bpmRef = useRef<number | null>(null);

    const playbackRafRef = useRef<number | null>(null);
    const lastPlaybackUiUpdateRef = useRef<number>(0);
    const progressListenersRef = useRef<{ element: HTMLAudioElement; cleanup: () => void } | null>(null);

    const syncPlaybackFromElement = useCallback((audioElement: HTMLAudioElement | null) => {
        if (!audioElement) {
            setPlayback({ currentTime: 0, duration: 0, progress: 0 });
            return;
        }

        const duration = Number.isFinite(audioElement.duration) ? audioElement.duration : 0;
        const currentTime = Number.isFinite(audioElement.currentTime) ? audioElement.currentTime : 0;
        const progress = duration > 0 ? Math.max(0, Math.min(currentTime / duration, 1)) : 0;

        setPlayback((prev) => {
            if (Math.abs(prev.currentTime - currentTime) < 0.05 && Math.abs(prev.duration - duration) < 0.05) return prev;
            return { currentTime, duration, progress };
        });
    }, []);

    const stopPlaybackLoop = useCallback(() => {
        if (playbackRafRef.current) {
            cancelAnimationFrame(playbackRafRef.current);
            playbackRafRef.current = null;
        }
    }, []);

    const startPlaybackLoop = useCallback(() => {
        stopPlaybackLoop();
        const tick = () => {
            const element = currentAudioElementRef.current;
            if (!element) {
                stopPlaybackLoop();
                return;
            }

            const now = performance.now();
            const minInterval = 1000 / PLAYBACK_UPDATE_FPS;
            if (now - lastPlaybackUiUpdateRef.current >= minInterval) {
                lastPlaybackUiUpdateRef.current = now;
                syncPlaybackFromElement(element);
            }

            if (!element.paused && !element.ended) {
                playbackRafRef.current = requestAnimationFrame(tick);
            } else {
                stopPlaybackLoop();
            }
        };

        playbackRafRef.current = requestAnimationFrame(tick);
    }, [stopPlaybackLoop, syncPlaybackFromElement]);

    const detachProgressListeners = useCallback(() => {
        progressListenersRef.current?.cleanup();
        progressListenersRef.current = null;
    }, []);

    const attachProgressListeners = useCallback((audioElement: HTMLAudioElement) => {
        detachProgressListeners();

        const onPlay = () => {
            syncPlaybackFromElement(audioElement);
            startPlaybackLoop();
        };
        const onPause = () => {
            try {
                audioElement.currentTime = 0;
            } catch { }
            syncPlaybackFromElement(audioElement);
            stopPlaybackLoop();
        };
        const onEnded = () => {
            syncPlaybackFromElement(audioElement);
            stopPlaybackLoop();
        };
        const onSeeked = () => syncPlaybackFromElement(audioElement);
        const onLoadedMetadata = () => syncPlaybackFromElement(audioElement);
        const onDurationChange = () => syncPlaybackFromElement(audioElement);

        audioElement.addEventListener("play", onPlay);
        audioElement.addEventListener("pause", onPause);
        audioElement.addEventListener("ended", onEnded);
        audioElement.addEventListener("seeked", onSeeked);
        audioElement.addEventListener("loadedmetadata", onLoadedMetadata);
        audioElement.addEventListener("durationchange", onDurationChange);

        progressListenersRef.current = {
            element: audioElement,
            cleanup: () => {
                audioElement.removeEventListener("play", onPlay);
                audioElement.removeEventListener("pause", onPause);
                audioElement.removeEventListener("ended", onEnded);
                audioElement.removeEventListener("seeked", onSeeked);
                audioElement.removeEventListener("loadedmetadata", onLoadedMetadata);
                audioElement.removeEventListener("durationchange", onDurationChange);
            },
        };
    }, [detachProgressListeners, startPlaybackLoop, stopPlaybackLoop, syncPlaybackFromElement]);

    const analyzeAudio = useCallback(() => {
        // Run only if eligible (min-width and tab visible) and analyser present
        if (!analyserRef.current || !isEnvironmentEligibleRef.current) {
            animationFrameRef.current = requestAnimationFrame(analyzeAudio);
            return;
        }

        const analyser = analyserRef.current;
        const bufferLength = analyser.frequencyBinCount;
        if (!dataArrayRef.current || dataArrayRef.current.length !== bufferLength) {
            dataArrayRef.current = new Uint8Array(bufferLength);
        }
        const dataArray = dataArrayRef.current as Uint8Array<ArrayBuffer>;
        analyser.getByteFrequencyData(dataArray);

        // Frequency band analysis
        const bassRange = Math.floor(bufferLength * 0.18); // widen bass band for stronger kick detection
        const midRange = Math.floor(bufferLength * 0.52); // extend mids slightly
        const highRange = bufferLength; // 40-100% (highs)

        // Calculate energy in each frequency band
        let bassEnergy = 0;
        let midEnergy = 0;
        let highEnergy = 0;

        for (let i = 0; i < bassRange; i++) {
            bassEnergy += dataArray[i];
        }
        for (let i = bassRange; i < midRange; i++) {
            midEnergy += dataArray[i];
        }
        for (let i = midRange; i < highRange; i++) {
            highEnergy += dataArray[i];
        }

        // Normalize energy values (0-1)
        bassEnergy = bassEnergy / (bassRange * 255);
        midEnergy = midEnergy / ((midRange - bassRange) * 255);
        highEnergy = highEnergy / ((highRange - midRange) * 255);

        // Overall energy for beat detection (bass-weighted)
        const totalEnergy = (bassEnergy * 2.2 + midEnergy * 1.0 + highEnergy * 0.25) / (2.2 + 1.0 + 0.25);

        // Beat detection algorithm
        energyHistoryRef.current.push(totalEnergy);
        if (energyHistoryRef.current.length > HISTORY_SIZE) {
            energyHistoryRef.current.shift();
        }

        const averageEnergy =
            energyHistoryRef.current.reduce((sum, val) => sum + val, 0) / energyHistoryRef.current.length;

        const variance =
            energyHistoryRef.current.reduce((sum, val) => sum + Math.pow(val - averageEnergy, 2), 0) /
            energyHistoryRef.current.length;

        // Sensitivity-adaptive thresholds
        const sens = Math.max(0.5, Math.min(visualizerSensitivity, 2.0));
        const threshold = (THRESHOLD_MULTIPLIER / sens) * averageEnergy;
        const now = performance.now();
        const timeSinceLastBeat = now - lastBeatTimeRef.current;
        const bpm = bpmRef.current;
        const msPerBeat = bpm ? 60000 / bpm : BASE_BEAT_MIN_INTERVAL_MS;
        const refractoryMs = Math.max(60, msPerBeat * (0.32 / sens)); // shorter refractory with higher sensitivity

        // Detect beat: energy spike above threshold with minimum time between beats
        const isBeat =
            totalEnergy > threshold &&
            totalEnergy > averageEnergy + Math.sqrt(variance) * VARIANCE_FACTOR &&
            timeSinceLastBeat > refractoryMs;

        if (isBeat) {
            lastBeatTimeRef.current = now;
            beatHistoryRef.current.push(totalEnergy);
            if (beatHistoryRef.current.length > 10) {
                beatHistoryRef.current.shift();
            }
            // Collect timestamps for BPM estimation
            beatTimestampsRef.current.push(now);
            if (beatTimestampsRef.current.length > 16) {
                beatTimestampsRef.current.shift();
            }
            // Estimate BPM from median inter-beat interval for robustness
            if (beatTimestampsRef.current.length >= 6) {
                const intervals: number[] = [];
                for (let i = 1; i < beatTimestampsRef.current.length; i++) {
                    intervals.push(beatTimestampsRef.current[i] - beatTimestampsRef.current[i - 1]);
                }
                intervals.sort((a, b) => a - b);
                const mid = Math.floor(intervals.length / 2);
                const medianInterval = intervals.length % 2 === 0 ? (intervals[mid - 1] + intervals[mid]) / 2 : intervals[mid];
                const estimatedBpm = Math.max(60, Math.min(190, 60000 / medianInterval));
                // simple smoothing to avoid jitter
                bpmRef.current = bpmRef.current ? (bpmRef.current * 0.85 + estimatedBpm * 0.15) : estimatedBpm;
            }
        }

        // Calculate beat intensity from energy ratio for wider dynamic range
        const ratio = averageEnergy > 0 ? totalEnergy / averageEnergy : 0;
        const beatIntensity = isBeat ? Math.max(0, Math.min((ratio - 1) * 1.75, 1)) : 0;

        // Throttle UI state updates to avoid React churn
        const nowTs = performance.now();
        const minInterval = 1000 / MAX_UPDATE_FPS;
        if (nowTs - lastUiUpdateRef.current >= minInterval) {
            lastUiUpdateRef.current = nowTs;
            setState((prev) => {
                // Only trigger React update if values changed meaningfully
                const b = Math.pow(bassEnergy, 1.2);
                const m = Math.pow(midEnergy, 1.2);
                const h = Math.pow(highEnergy, 1.2);
                const epsilon = 0.01;
                const changed =
                    Math.abs(prev.beatIntensity - beatIntensity) > epsilon ||
                    Math.abs(prev.bassEnergy - b) > epsilon ||
                    Math.abs(prev.midEnergy - m) > epsilon ||
                    Math.abs(prev.highEnergy - h) > epsilon ||
                    prev.isBeat !== isBeat ||
                    prev.isPlaying !== true;
                if (!changed) return prev;
                return {
                    isPlaying: true,
                    beatIntensity,
                    bassEnergy: b,
                    midEnergy: m,
                    highEnergy: h,
                    isBeat,
                };
            });
        }

        animationFrameRef.current = requestAnimationFrame(analyzeAudio);
    }, []);

    const registerAudioElement = useCallback(
        (audioElement: HTMLAudioElement) => {
            try {
                // Create or reuse AudioContext
                if (!audioContextRef.current) {
                    audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
                }
                const audioContext = audioContextRef.current;
                // Resume context if suspended (browser autoplay policy)
                if (audioContext.state === "suspended") {
                    audioContext.resume();
                }
                // Create analyser if needed
                if (!analyserRef.current) {
                    analyserRef.current = audioContext.createAnalyser();
                    analyserRef.current.fftSize = FFT_SIZE;
                    analyserRef.current.smoothingTimeConstant = SMOOTHING;
                }
                // Only create source node once per audio element
                let sourceNode = sourceNodeMapRef.current.get(audioElement);
                if (!sourceNode) {
                    sourceNode = audioContext.createMediaElementSource(audioElement);
                    sourceNodeMapRef.current.set(audioElement, sourceNode);
                }
                // If switching audio elements, disconnect previous
                if (currentAudioElementRef.current && currentAudioElementRef.current !== audioElement && sourceRef.current) {
                    sourceRef.current.disconnect();
                }
                sourceRef.current = sourceNode;
                currentAudioElementRef.current = audioElement;

                attachProgressListeners(audioElement);
                syncPlaybackFromElement(audioElement);
                if (!audioElement.paused && !audioElement.ended) {
                    startPlaybackLoop();
                }
                // Connect if not already connected
                if (sourceRef.current && analyserRef.current) {
                    try {
                        sourceRef.current.connect(analyserRef.current);
                    } catch (e) { }
                    try {
                        analyserRef.current.connect(audioContext.destination);
                    } catch (e) { }
                }
                // Start analysis if environment is eligible
                if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current);
                }
                analyzeAudio();
            } catch (error) {
                console.error("Error setting up audio visualization:", error);
            }
        },
        [analyzeAudio, attachProgressListeners, startPlaybackLoop, syncPlaybackFromElement]
    );

    const unregisterAudioElement = useCallback(() => {
        stopPlaybackLoop();
        detachProgressListeners();
        setPlayback({ currentTime: 0, duration: 0, progress: 0 });
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
        if (sourceRef.current) {
            try {
                sourceRef.current.disconnect();
            } catch (e) { }
            // Do NOT delete from sourceNodeMapRef; node must persist for element lifetime
            sourceRef.current = null;
        }
        currentAudioElementRef.current = null;
        setState({
            isPlaying: false,
            beatIntensity: 0,
            bassEnergy: 0,
            midEnergy: 0,
            highEnergy: 0,
            isBeat: false,
        });
        // Reset history
        beatHistoryRef.current = [];
        energyHistoryRef.current = [];
        lastBeatTimeRef.current = 0;
        beatTimestampsRef.current = [];
        bpmRef.current = null;
    }, [detachProgressListeners, stopPlaybackLoop]);

    // Cleanup on unmount
    useEffect(() => {
        // Prepare media query gating and visibility gating
        const mq = window.matchMedia(`(min-width: ${MIN_WIDTH_PX}px)`);
        mediaQueryRef.current = mq;

        const recomputeEligibility = () => {
            const visible = document.visibilityState === "visible";
            isEnvironmentEligibleRef.current = mq.matches && visible;
        };
        recomputeEligibility();
        const mqListener = () => recomputeEligibility();
        const visListener = () => recomputeEligibility();
        // Modern addEventListener supported; fallback for older Safari
        if (typeof mq.addEventListener === "function") {
            mq.addEventListener("change", mqListener);
        } else if (typeof (mq as any).addListener === "function") {
            (mq as any).addListener(mqListener);
        }
        document.addEventListener("visibilitychange", visListener);

        // If environment becomes ineligible, pause analysis loop
        const interval = setInterval(() => {
            // Soft guard to stop analysis if tab hidden or width too small
            if (!isEnvironmentEligibleRef.current && animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            } else if (isEnvironmentEligibleRef.current && !animationFrameRef.current) {
                // If eligible again, resume only if a source exists
                if (sourceRef.current && analyserRef.current) {
                    animationFrameRef.current = requestAnimationFrame(analyzeAudio);
                }
            }
        }, 250);

        return () => {
            clearInterval(interval);
            if (typeof mq.removeEventListener === "function") {
                mq.removeEventListener("change", mqListener);
            } else if (typeof (mq as any).removeListener === "function") {
                (mq as any).removeListener(mqListener);
            }
            document.removeEventListener("visibilitychange", visListener);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            if (sourceRef.current) {
                sourceRef.current.disconnect();
            }
            if (audioContextRef.current && audioContextRef.current.state !== "closed") {
                audioContextRef.current.close();
            }
        };
    }, []);

    return (
        <AudioVisualizationContext.Provider
            value={{
                ...state,
                registerAudioElement,
                unregisterAudioElement,
                    playbackCurrentTime: playback.currentTime,
                    playbackDuration: playback.duration,
                    playbackProgress: playback.progress,
                visualizerSensitivity,
                setVisualizerSensitivity,
                bpmEstimate: bpmRef.current,
                visualizerRange,
                setVisualizerRange,
                sensitivityMin,
                sensitivityMax,
                rangeMin,
                rangeMax,
            }}
        >
            {children}
        </AudioVisualizationContext.Provider>
    );
}

export function useAudioVisualization() {
    const context = useContext(AudioVisualizationContext);
    if (!context) {
        throw new Error("useAudioVisualization must be used within AudioVisualizationProvider");
    }
    return context;
}
